// ABOUTME: Structured tooltip content for training zone descriptions
// ABOUTME: Shows zone number with color indicator, name, and detailed description

import { memo } from "react"
import { useTranslation } from "react-i18next"
import { ZONE_COLORS } from "@shared/constants"
import { TooltipSection } from "./TooltipSection"
import { TooltipHighlight } from "./TooltipHighlight"

interface ZoneTooltipContentProps {
  zoneNumber: number
  zoneName?: string
  description: string
  keyFeature?: string
  importantNote?: string
  className?: string
}

export const ZoneTooltipContent = memo(
  ({ zoneNumber, zoneName, description, keyFeature, importantNote, className = "" }: ZoneTooltipContentProps) => {
    const { t } = useTranslation()
    const key = zoneNumber as keyof typeof ZONE_COLORS
    const zoneColor = key in ZONE_COLORS ? ZONE_COLORS[key] : ZONE_COLORS[1]

    return (
      <div className={`max-w-[280px] min-w-[220px] ${className}`}>
        {/* Header with zone indicator */}
        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-600">
          <div
            className="w-4 h-4 rounded flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-white"
            style={{ backgroundColor: zoneColor }}
          >
            {zoneNumber}
          </div>
          <div className="flex-1 min-w-0">
            <span className="font-semibold text-[13px]">
              {t("common.zone")} {zoneNumber}
            </span>
            {zoneName && <span className="text-gray-400 text-[11px] ml-1">— {zoneName}</span>}
          </div>
        </div>

        {/* Key feature if provided */}
        {keyFeature && (
          <TooltipSection
            title={t("tooltip.keyFeature", "Key feature")}
            titleColor="text-gray-500"
          >
            {keyFeature}
          </TooltipSection>
        )}

        {/* Main description */}
        <TooltipSection>{description}</TooltipSection>

        {/* Important note if provided */}
        {importantNote && <TooltipHighlight type="info">{importantNote}</TooltipHighlight>}
      </div>
    )
  },
)

ZoneTooltipContent.displayName = "ZoneTooltipContent"

// Simplified version that parses description to extract key points
interface SimpleZoneTooltipProps {
  zoneNumber: number
  zoneName?: string
  tooltipKey: string
}

export const SimpleZoneTooltip = memo(({ zoneNumber, zoneName, tooltipKey }: SimpleZoneTooltipProps) => {
  const { t } = useTranslation()
  const description = t(tooltipKey)

  return (
    <ZoneTooltipContent
      zoneNumber={zoneNumber}
      zoneName={zoneName}
      description={description}
    />
  )
})

SimpleZoneTooltip.displayName = "SimpleZoneTooltip"
